import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { nvStorage } from '@simwood/nv-react-components';
import { clearAuthToken, nvFetch } from '../helpers/nvFetch';
import { isWeb } from '../helpers/platform';
import type { ThunkAPI } from '../store';
import { fetchBranding, finaliseLogout } from '../store/thunks';
import { ApiUser } from '../types/User';

export interface UserState {
  accessToken?: string;
  isAuthenticated: boolean;
  isAuthTested: boolean;
  isFetchingUser: boolean;
  isLoggingInExternally: boolean;
  error?: SerializedError;
  user?: ApiUser;
}

const initialState: UserState = {
  accessToken: nvStorage.get('token'),
  isAuthenticated: false,
  isAuthTested: false,
  isFetchingUser: false,
  isLoggingInExternally: false,
  error: undefined,
  user: undefined,
};

export const testAuthAndFetchUser = createAsyncThunk<ApiUser, void, ThunkAPI>(
  'client/testAuthAndFetchUser',
  async (_, thunkAPI) => {
    const accessToken = thunkAPI.getState().user.accessToken;
    if (!accessToken) {
      // reject
      throw new Error();
    }
    const fetchRes = await nvFetch(`/users/me`, {
      token: accessToken,
    });
    const user = (await fetchRes.json()) as ApiUser;

    if (!isWeb()) {
      const base64Url = accessToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );

      const parsed = JSON.parse(jsonPayload);
      thunkAPI.dispatch(fetchBranding(parsed.rid));
    }

    return user;
  },
);

// logoutFactory is used by both the Communicator and Bridge apps, each with its own
// combined store/dispatch type, so TApi (the app-specific ThunkAPI) can't be threaded
// through createAsyncThunk's own generics - RTK can't resolve GetThunkAPI<TApi> back to
// TApi for an as-yet-uninstantiated generic. dispatch/state are typed concretely instead,
// and the cast to TApi at the finalise() boundary is the one place callers' concrete
// dispatch type takes over.
type LogoutThunkApi = {
  state: unknown;
  dispatch: (action: any) => any;
};

export const logoutFactory = <TApi extends LogoutThunkApi>(
  finalise?: (thunkAPI: TApi) => void,
) =>
  createAsyncThunk<void, void, LogoutThunkApi>(
    'user/logout',
    async (_, thunkAPI) => {
      nvStorage.remove('token');
      nvStorage.remove('token-remember');
      nvStorage.remove('token-expires');
      nvStorage.remove('selectedCustomerId');

      if (finalise) {
        finalise(thunkAPI as unknown as TApi);
      } else {
        thunkAPI.dispatch(finaliseLogout());
      }
    },
  );

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    tokenReceived: (
      state,
      action: PayloadAction<{ accessToken: string; expires: string }>,
    ) => {
      const { accessToken } = action.payload;
      state.accessToken = accessToken;
    },
    externalLoginStarted: (state) => {
      state.isLoggingInExternally = true;
    },
    externalLoginFinished: (state) => {
      state.isLoggingInExternally = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(testAuthAndFetchUser.pending, (state) => {
        state.isAuthTested = false;
      })
      .addCase(
        testAuthAndFetchUser.fulfilled,
        (state, action: PayloadAction<ApiUser>) => {
          const user = action.payload;
          const { id, accessLevel, username, uri, email, firstName, lastName } =
            user;
          Sentry.getCurrentScope().setUser({
            id,
            email,
            username,
            Name: `${firstName} ${lastName}`,
            'Access Level': accessLevel,
            URI: uri,
          });
          state.isFetchingUser = false;
          state.user = user;
          state.isAuthTested = true;
          state.isAuthenticated = true;
          state.error = undefined;
        },
      )
      .addCase(testAuthAndFetchUser.rejected, (state) => {
        state.isAuthTested = true;
        clearAuthToken();
      })
      .addCase(finaliseLogout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.accessToken = undefined;
        state.user = undefined;
      });
  },
});

export const { tokenReceived, externalLoginStarted, externalLoginFinished } =
  userSlice.actions;

export default userSlice.reducer;
